import { memo, useMemo } from 'react';

import { useBreakpointsContext } from '@air/provider-media-query';

import { PrivateBoardFiltersButton } from '~/components/Filters/FiltersButton/PrivateBoardFiltersButton';
import { PrivateFiltersBar } from '~/components/Filters/v2/components/PrivateFiltersBar';
import {
  PrivateHeaderBar,
  type PrivateHeaderBarProps,
} from '~/components/Layouts/HomeLayout/HomeLayoutHeader/PrivateHeaderBar';
import { MobileSearchActiveFilterButton } from '~/components/Mobile/Topbar/MobileSearchActiveFilterButton';
import { PrivateHeaderSearchRow } from '~/components/Search/PrivateHeaderSearchRow';
import { PrivatePageSearch } from '~/components/Search/PrivatePageSearch';
import { useAppliedFiltersCount } from '~/hooks/filters/useAppliedFiltersCount';
import { useSearchParams } from '~/hooks/search/useSearchParams';

export type AllContentHeaderBarProps = {
  headerRef: PrivateHeaderBarProps['headerRef'];
};

export const AllContentHeaderBar = memo(({ headerRef }: AllContentHeaderBarProps) => {
  const { filtersCount } = useAppliedFiltersCount();
  const { search } = useSearchParams();
  const { isAboveMediumScreen } = useBreakpointsContext();

  const footer = useMemo(() => {
    return (
      <PrivateFiltersBar
        className="-mt-px ml-px pt-0"
        trackLocation="All assets & files"
        // Only show boards filter if search is not empty
        availableFilterTypes={search ? ['assets', 'files', 'boards'] : ['assets', 'files']}
      />
    );
  }, [search]);

  return (
    <PrivateHeaderBar footer={isAboveMediumScreen ? footer : null} headerRef={headerRef}>
      <PrivateHeaderSearchRow
        isSearchOpen={!isAboveMediumScreen}
        Search={PrivatePageSearch}
        trackSearchBarLocation="All assets & files"
        searchPlaceholder="Search all assets & files"
        title={filtersCount ? 'All assets & files' : undefined}
        filtersButton={
          !isAboveMediumScreen && (
            <PrivateBoardFiltersButton
              renderTrigger={(props) => <MobileSearchActiveFilterButton {...props} />}
              title="Filter all assets & files"
            />
          )
        }
      />
    </PrivateHeaderBar>
  );
});

AllContentHeaderBar.displayName = 'AllContentHeaderBar';
